import { dispatchEvent } from './adapters';
import { enrich } from './enrich';
import {
  type AnalyticsDebugHook,
  type AnalyticsDebuggerEvent,
  type TrackEvent,
} from './types';

/**
 * `track()` — the analytics pipeline entry point (Wave 1 · P2).
 * Enrich once (single source), notify the debug hook, fan out to the
 * registered adapters honoring the routing options.
 */

let debugHook: AnalyticsDebugHook | undefined;

/**
 * App wiring passes the existing debugger (`logTrackedAnalyticsEvent`) here —
 * the debugger itself stays app-side (it reads the Amplitude user id and the
 * inspector store, both app/SDK-coupled). Payload shape is unchanged.
 */
export function registerAnalyticsDebugHook(hook: AnalyticsDebugHook | undefined): void {
  debugHook = hook;
}

export function track(trackingEvent: TrackEvent): void {
  const { route, payloads } = enrich(trackingEvent);

  if (debugHook) {
    // Mirror the legacy debugger array: optional destinations first (only
    // when routed), then the always-on pair; props shallow-copied like the
    // effect did.
    const order = ['posthog', 'amplitude', 'segment', 'datadog', 'snowplow'] as const;
    const destinationEvents: AnalyticsDebuggerEvent[] = [];
    for (const destination of order) {
      if (!route.has(destination)) continue;
      const payload = payloads.get(destination);
      if (!payload) continue;
      destinationEvents.push({
        destination,
        eventName: payload.event,
        props: { ...payload.props },
      });
    }
    debugHook({ eventName: trackingEvent.event_name, destinationEvents });
  }

  dispatchEvent(route, payloads);
}

/** Test-only: reset the debug hook between tests. */
export function __resetAnalyticsDebugHookForTests(): void {
  debugHook = undefined;
}
